import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export const PAGE_SIZE_OPTIONS = [10, 20, 50];

export interface UsePaginationOptions {
  defaultPageSize?: number;
  pageParam?: string;
  sizeParam?: string;
}

export interface UsePaginationResult {
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
}

/**
 * Keeps the current page (and page size) in sync with the URL query string so
 * pagination state survives reloads and is shareable. Each view can override
 * the param names if more than one paginated list ever lives on the same route.
 */
export function usePagination(options: UsePaginationOptions = {}): UsePaginationResult {
  const {
    defaultPageSize = 20,
    pageParam = 'page',
    sizeParam = 'pageSize',
  } = options;

  const [searchParams, setSearchParams] = useSearchParams();

  const parsedPage = parseInt(searchParams.get(pageParam) || '', 10);
  const page = Number.isNaN(parsedPage) || parsedPage < 1 ? 1 : parsedPage;

  const parsedSize = parseInt(searchParams.get(sizeParam) || '', 10);
  const pageSize = Number.isNaN(parsedSize) || parsedSize < 1 ? defaultPageSize : parsedSize;

  const setPage = useCallback(
    (next: number) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set(pageParam, String(Math.max(1, next)));
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams, pageParam],
  );

  const setPageSize = useCallback(
    (size: number) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set(sizeParam, String(size));
          params.set(pageParam, '1');
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams, sizeParam, pageParam],
  );

  return { page, pageSize, setPage, setPageSize };
}
